export class Pagination extends HTMLElement {
    static observedAttributes = ['per-page', 'total-items'];

    static DEFAULTS = {
        perPage: 10,
        totalItems: 10,
    };

    constructor() {
        super();

        this.handlePageClick = this.handlePageClick.bind(this);
        this.previousPage = this.previousPage.bind(this);
        this.nextPage = this.nextPage.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.initialized = false;

        this.langObserver = new MutationObserver((mutationsList) => {
            for (const mutation of mutationsList) {
                if (
                    mutation.type === 'attributes' &&
                    mutation.attributeName === 'lang'
                ) {
                    this.lang =
                        document.documentElement.getAttribute('lang') || 'en';
                    this.updateTranslations();
                }
            }
        });
    }

    connectedCallback() {
        this.shadow = this.attachShadow({ mode: 'open' });

        this.currentPage = 1;
        this.lang = document.documentElement.getAttribute('lang') || 'en';
        this.langObserver.observe(document.documentElement, {
            attributes: true,
        });

        this.addStyles();
        this.mapAttributesToVariables();
        this.createElements();
        this.addEventListeners();

        this.initialized = true;
    }

    disconnectedCallback() {
        this.removeEventListeners();
        this.langObserver.disconnect();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue) {
            return;
        }

        const parsedValue = Number(newValue);
        if (name === 'per-page') {
            this.perPage =
                !isNaN(parsedValue) && parsedValue > 0
                    ? parsedValue
                    : Pagination.DEFAULTS.perPage;
        } else if (name === 'total-items') {
            this.totalItems =
                !isNaN(parsedValue) && parsedValue >= 0
                    ? parsedValue
                    : Pagination.DEFAULTS.totalItems;
        } else if (name === 'aria-label' && newValue.length > 0) {
            const paginationContainer = this._shadow.querySelector('nav');
            if (!paginationContainer) {
                console.warn(
                    'Pagination container not found. Cannot set aria-label.'
                );
                return;
            }

            paginationContainer.setAttribute('aria-label', newValue);
            this.removeAttribute('aria-label');
        }

        if (this.initialized) {
            this.updatePages();
            this.updateLinks();
        }
    }

    mapAttributesToVariables() {
        if (this.hasAttribute('per-page')) {
            this.perPage = Number(this.getAttribute('per-page'));
            if (isNaN(this.perPage) || this.perPage <= 0) {
                console.warn(
                    `Invalid "per-page" attribute. Falling back to default: ${Pagination.DEFAULTS.perPage}.`
                );
                this.perPage = Pagination.DEFAULTS.perPage;
            }
        } else {
            this.perPage = Pagination.DEFAULTS.perPage;
            console.warn(
                `Pagination: Missing "per-page" attribute, default set to ${Pagination.DEFAULTS.perPage}. This may have unwanted consequences.`
            );
        }
        if (this.hasAttribute('total-items')) {
            this.totalItems = Number(this.getAttribute('total-items'));
            if (isNaN(this.totalItems) || this.totalItems <= 0) {
                console.warn(
                    `Invalid "total-items" attribute. Falling back to default: ${Pagination.DEFAULTS.totalItems}.`
                );
                this.perPage = Pagination.DEFAULTS.perPage;
            }
        } else {
            this.totalItems = Pagination.DEFAULTS.totalItems;
            console.warn(
                `Pagination: Missing "total-items" attribute, default set to ${Pagination.DEFAULTS.totalItems}. This may have unwanted consequences.`
            );
        }
    }

    createElements() {
        const paginationContainer = document.createElement('nav');
        paginationContainer.classList.add('pagination');

        if (this.hasAttribute('aria-label')) {
            paginationContainer.setAttribute(
                'aria-label',
                this.getAttribute('aria-label')
            );
            this.removeAttribute('aria-label');
        } else {
            paginationContainer.setAttribute(
                'aria-label',
                this.translate('Pagination controls')
            );
        }

        const prevContainer = this.createPreviousLink();
        const nextContainer = this.createNextLink();

        this.paginationNumberContainer = document.createElement('ul');
        this.paginationNumberContainer.classList.add('pagination-numbers');

        paginationContainer.appendChild(prevContainer);
        paginationContainer.appendChild(this.paginationNumberContainer);
        paginationContainer.appendChild(nextContainer);

        this.shadow.appendChild(paginationContainer);

        this.liveRegion = document.createElement('div');
        this.liveRegion.setAttribute('aria-live', 'polite');
        this.liveRegion.setAttribute('aria-atomic', 'true');
        this.liveRegion.classList.add('sr-only');
        this.shadow.appendChild(this.liveRegion);

        this.updatePages();
        this.updateLinks();
    }

    createPreviousLink() {
        const prevContainer = document.createElement('div');

        this.linkPrev = document.createElement('a');
        prevContainer.appendChild(this.linkPrev);
        this.linkPrev.classList.add('pagination-link');
        this.linkPrev.classList.add('prev');
        this.linkPrev.setAttribute('href', 'javascript:void(0);');
        this.linkPrev.setAttribute('role', 'button');
        this.linkPrev.setAttribute(
            'aria-label',
            this.translate('Previous page')
        );
        this.linkPrev.addEventListener('keydown', this.handleKeyDown);

        this.linkPrev.innerHTML = `
        <svg class="chevron-left" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.4733 5.56906C15.6327 5.63746 15.766 5.75201 15.8586 5.8954C15.9498 6.03674 15.9999 6.20297 16 6.37424C16.0001 6.48713 15.9786 6.59917 15.9366 6.70401C15.8947 6.80876 15.8327 6.90514 15.7534 6.9871L10.9183 11.9997L15.7537 17.0125C15.9133 17.1779 15.9999 17.3985 15.9999 17.6249C15.9999 17.8513 15.9133 18.0719 15.7537 18.2373C15.5936 18.4033 15.3724 18.5 15.138 18.5C14.9035 18.5 14.6824 18.4033 14.5223 18.2373L9.35199 12.8773C9.12471 12.6416 9 12.3257 9 11.9999C9 11.6741 9.12469 11.3582 9.35195 11.1226L14.5222 5.76254C14.6415 5.63905 14.7959 5.55258 14.9664 5.51747C15.1371 5.48233 15.3137 5.50055 15.4733 5.56906Z" fill="currentColor"/>
        </svg>
        `;

        const linkPrevText = document.createElement('span');
        linkPrevText.innerText = this.translate('Previous');

        this.linkPrev.appendChild(linkPrevText);

        return prevContainer;
    }

    createNextLink() {
        const nextContainer = document.createElement('div');

        this.linkNext = document.createElement('a');
        nextContainer.appendChild(this.linkNext);
        this.linkNext.classList.add('pagination-link');
        this.linkNext.classList.add('next');
        this.linkNext.setAttribute('href', 'javascript:void(0);');
        this.linkNext.setAttribute('role', 'button');
        this.linkNext.setAttribute('aria-label', this.translate('Next page'));
        this.linkNext.addEventListener('keydown', this.handleKeyDown);

        this.linkNext.innerHTML = `
        <svg class="chevron-right" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\
            <path d="M8.74628 5.76272C8.90644 5.5967 9.12756 5.5 9.36203 5.5C9.59653 5.5 9.81761 5.59673 9.97774 5.76272L15.148 11.1227C15.3753 11.3584 15.5 11.6743 15.5 12.0001C15.5 12.3259 15.3753 12.6418 15.1481 12.8774L9.97775 18.2375C9.85847 18.361 9.70412 18.4474 9.53359 18.4825C9.36294 18.5177 9.18626 18.4995 9.02665 18.4309C8.86729 18.3625 8.73397 18.248 8.64144 18.1046C8.55023 17.9633 8.50015 17.797 8.5 17.6258C8.49992 17.5129 8.5214 17.4008 8.56339 17.296C8.60536 17.1912 8.66734 17.0947 8.74672 17.0127L13.5817 12.0003L8.74628 6.98751C8.58674 6.82212 8.50015 6.60151 8.50015 6.37512C8.50015 6.14874 8.58672 5.92812 8.74628 5.76272Z" fill="currentColor" />\
        </svg>`;

        const linkNextText = document.createElement('span');
        linkNextText.innerText = this.translate('Next');

        this.linkNext.appendChild(linkNextText);

        return nextContainer;
    }

    addStyles() {
        const style = document.createElement('style');

        style.textContent = `
        :host {
            font-family: 'Asap';
        }

        * {
           font-family: 'Asap';
        }

        .pagination {
            display: flex;
            justify-content: center;
            align-items: center;
            column-gap: .5rem;
        }

        ul.pagination-numbers {
            display: flex;
            list-style: none;
            justify-content: center;
            padding: 0;
            column-gap: 0.5rem;
        }

        ul.pagination-numbers a {
            width: 2rem;
            height: 2rem;
        }

        ul.pagination-numbers li:has(.ellipsis) {
            display: inline-flex;
            align-items: anchor-center;
        }
        .ellipsis {
            font-size: 1rem;
        }

        .pagination a {
            display: inline-flex;
            justify-content: center;
            align-items: center;
            background-color: transparent;
            color: var(--svea-interactive-primary, #00699a);
            font-size: .875rem;
            font-weight: 500;
            line-height: 1.5rem;
            text-decoration: underline;
            text-underline-offset: 2px;
            text-transform: capitalize;
            border: 0;
            border-radius: .5rem;
            cursor: pointer;
        }

        .pagination > div > a {
            width: 2rem;
            height: 2rem;
            border: 1px solid var(--svea-border-subdued, #949494);
            padding: 0;
        }

        a span {
            display: none;
        }

        @media only screen and (min-width: 576px) {
            .chevron-left,
            .chevron-right {
                display: none;
            }

            a span {
                display: block;
            }

            .pagination > div a {
                width: auto;
                border: 0;
                padding: .125rem .75rem 0 .75rem;
            }
        }

        .pagination a.active {
            background: var(--svea--background-dark, #00325c);
            color: #fff;
        }

        .chevron-left,
        .chevron-right {
            width: 1.5rem;
            height: 1.5rem;
        }

        .pagination a:focus-visible {
            outline: 2px solid var(--svea-interactive-primary-active, #115475);
            outline-offset: 2px;
        }
            
        .sr-only {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border-width: 0;
        }
        `;

        this.shadow.appendChild(style);
    }
    createLink(value, isLastPage) {
        const listItem = document.createElement('li');
        const link = document.createElement('a');
        link.textContent = value;

        link.setAttribute('href', 'javascript:void(0);');
        if (value === this.currentPage) {
            link.classList.add('active');
            link.setAttribute('aria-current', 'page');
        }
        link.setAttribute('type', 'button');
        link.setAttribute('role', 'button');
        link.setAttribute(
            'aria-label',
            isLastPage
                ? `${this.translate('Page')} ${value}, ${this.translate('Last page')}`
                : `${this.translate('Page')} ${value}`
        );
        link.setAttribute('data-page', value);

        link.addEventListener('click', this.handlePageClick);
        link.addEventListener('keydown', this.handleKeyDown);

        listItem.appendChild(link);

        this.paginationNumberContainer.appendChild(listItem);
    }

    createEllipsis() {
        const listItem = document.createElement('li');
        const ellipsis = document.createElement('span');
        ellipsis.innerText = '...';
        ellipsis.classList.add('ellipsis');
        listItem.setAttribute(
            'aria-label',
            this.translate('Ellipsis indicating non visible pages')
        );
        listItem.appendChild(ellipsis);
        this.paginationNumberContainer.appendChild(listItem);
    }

    createNumbers() {
        this.paginationNumberContainer.replaceChildren();

        if (this.totalPages <= 5) {
            for (let i = 1; i <= this.totalPages; i++) {
                this.createLink(i);
            }
        } else {
            this.createLink(1);

            if (this.currentPage > 3) {
                this.createEllipsis();
            }

            const start = Math.max(2, this.currentPage - 1);
            const end = Math.min(this.totalPages - 1, this.currentPage + 1);

            const middlePages = [];
            for (let i = start; i <= end; i++) {
                middlePages.push(i);
            }

            while (middlePages.length < 3 && middlePages[0] > 2) {
                middlePages.unshift(middlePages[0] - 1);
            }
            while (
                middlePages.length < 3 &&
                middlePages[middlePages.length - 1] < this.totalPages - 1
            ) {
                middlePages.push(middlePages[middlePages.length - 1] + 1);
            }

            middlePages.forEach((page) => this.createLink(page));

            if (this.currentPage < this.totalPages - 2) {
                this.createEllipsis();
            }

            this.createLink(this.totalPages, true);
        }
    }

    updatePages() {
        this.currentPage = 1;

        this.totalPages = Math.ceil(this.totalItems / this.perPage);
    }

    updateLinks() {
        this.createNumbers();
        this.updateVisibility();
    }

    updateVisibility() {
        if (this.totalPages === 1) {
            this.style = 'display: none';
        } else {
            this.style = '';
        }

        if (this.currentPage === 1) {
            this.hideLink(this.linkPrev);
            this.showLink(this.linkNext);
        } else if (this.currentPage === this.totalPages) {
            this.showLink(this.linkPrev);
            this.hideLink(this.linkNext);
        } else {
            this.showLink(this.linkPrev);
            this.showLink(this.linkNext);
        }
    }

    showLink(link) {
        link.removeAttribute('inert');

        link.style.opacity = '1';
        link.style.visibility = 'visible';
    }

    hideLink(link) {
        link.setAttribute('inert', '');

        link.style.opacity = '0';
        link.style.visibility = 'hidden';
    }

    toPage(number) {
        if (number === 0 || number > this.totalPages) {
            return;
        }

        const previousPage = this.currentPage;
        this.currentPage = number;
        this.updateLinks();

        this.announcePageChange(previousPage, this.currentPage);
        this.emit('pagination:pageChanged', { page: number });

        setTimeout(() => {
            const currentPageLink = this.shadow.querySelector(
                '.pagination-numbers a.active'
            );
            if (currentPageLink) {
                currentPageLink.focus();
            }
        }, 0);
    }

    nextPage() {
        this.toPage(this.currentPage + 1);
    }

    previousPage() {
        this.toPage(this.currentPage - 1);
    }

    handlePageClick(event) {
        const pageValue = Number(event.target.getAttribute('data-page'));
        if (!isNaN(pageValue)) {
            this.toPage(pageValue);
        }
    }

    handleKeyDown(event) {
        const key = event.key;
        const target = event.target;

        switch (key) {
            case 'Enter':
            case ' ':
                event.preventDefault();
                if (target === this.linkPrev) {
                    this.previousPage();
                } else if (target === this.linkNext) {
                    this.nextPage();
                } else {
                    const pageValue = Number(target.getAttribute('data-page'));
                    if (!isNaN(pageValue)) {
                        this.toPage(pageValue);
                    }
                }
                break;

            case 'ArrowLeft':
                event.preventDefault();
                if (this.currentPage > 1) {
                    this.previousPage();
                }
                break;

            case 'ArrowRight':
                event.preventDefault();
                if (this.currentPage < this.totalPages) {
                    this.nextPage();
                }
                break;

            case 'Home':
                event.preventDefault();
                this.toPage(1);
                break;

            case 'End':
                event.preventDefault();
                this.toPage(this.totalPages);
                break;

            case 'Tab':
                break;

            default:
                break;
        }
    }

    addEventListeners() {
        this.linkPrev.addEventListener('click', this.previousPage);
        this.linkNext.addEventListener('click', this.nextPage);

        document.addEventListener('keydown', (event) => {
            if (event.altKey && event.key === 'PageUp') {
                event.preventDefault();
                this.previousPage();
            } else if (event.altKey && event.key === 'PageDown') {
                event.preventDefault();
                this.nextPage();
            }
        });
    }

    removeEventListeners() {
        this.linkPrev.removeEventListener('click', this.previousPage);
        this.linkNext.removeEventListener('click', this.nextPage);

        const pageLinks = this.shadow.querySelectorAll('.pagination-numbers a');
        pageLinks.forEach((link) => {
            link.removeEventListener('keydown', this.handleKeyDown);
        });
    }

    emit(type, detail = {}) {
        let event = new CustomEvent(type, {
            bubbles: true,
            cancelable: true,
            detail: detail,
        });

        this.dispatchEvent(event);
    }

    announcePageChange(previousPage, currentPage) {
        let message = '';

        if (currentPage > previousPage) {
            message = `${this.translate('Navigated to next page')}: ${currentPage} ${this.translate('of')} ${this.totalPages}`;
        } else if (currentPage < previousPage) {
            message = `${this.translate('Navigated to previous page')}: ${currentPage} ${this.translate('of')} ${this.totalPages}`;
        } else {
            message = `${this.translate('Page')} ${currentPage} ${this.translate('of')} ${this.totalPages}`;
        }

        this.liveRegion.textContent = '';

        setTimeout(() => {
            this.liveRegion.textContent = message;
        }, 50);
    }

    translate(text) {
        if (this.lang === 'en') {
            return text;
        }

        const translations = {
            'Pagination controls': 'Sidnavigering',
            'Previous page': 'Föregående sida',
            'Next page': 'Nästa sida',
            Previous: 'Föregående',
            Next: 'Nästa',
            Page: 'Sida',
            'Last page': 'Sista sidan',
            of: 'av',
            'Navigated to next page': 'Navigerade till nästa sida',
            'Navigated to previous page': 'Navigerade till föregående sida',
            'Ellipsis indicating non visible pages':
                'Tre punkter som visar att fler sidor finns',
        };

        if (!translations[text]) {
            console.warn(
                `Missing translation for "${text}" in ${this.lang} language.`
            );
        }

        return translations[text] || text;
    }

    updateTranslations() {
        this.linkPrev.setAttribute(
            'aria-label',
            this.translate('Previous page')
        );
        this.linkPrev.querySelector('span').textContent =
            this.translate('Previous');

        this.linkNext.setAttribute('aria-label', this.translate('Next page'));
        this.linkNext.querySelector('span').textContent =
            this.translate('Next');

        const pageLinks = this.shadow.querySelectorAll('.pagination-numbers a');
        pageLinks.forEach((link) => {
            const pageValue = link.getAttribute('data-page');
            const isLastPage = pageValue == this.totalPages;
            link.setAttribute(
                'aria-label',
                isLastPage
                    ? `${this.translate('Page')} ${pageValue}, ${this.translate('Last page')}`
                    : `${this.translate('Page')} ${pageValue}`
            );
        });

        const ellipses = this.shadow.querySelectorAll('.ellipsis');
        ellipses.forEach((ellipsis) => {
            ellipsis.parentElement.setAttribute(
                'aria-label',
                this.translate('Ellipsis indicating non visible pages')
            );
        });

        this.liveRegion.textContent = '';
    }
}

customElements.define('svea-pagination', Pagination);
